import React from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StarRatingProps {
  rating: number | null;
  maxRating?: number;
  size?: 'sm' | 'md' | 'lg';
  showValue?: boolean;
  className?: string;
}

const StarRating: React.FC<StarRatingProps> = ({
  rating,
  maxRating = 5,
  size = 'sm',
  showValue = true,
  className,
}) => {
  if (rating === null || rating === undefined) {
    return (
      <div className={cn('flex items-center gap-1 text-earth-400', className)}>
        <Star className={cn(
          'text-earth-300',
          size === 'sm' && 'h-3.5 w-3.5',
          size === 'md' && 'h-4 w-4',
          size === 'lg' && 'h-5 w-5',
        )} />
        <span className={cn(
          'text-earth-400',
          size === 'sm' && 'text-xs',
          size === 'md' && 'text-sm',
          size === 'lg' && 'text-base',
        )}>-</span>
      </div>
    );
  }

  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = maxRating - fullStars - (hasHalfStar ? 1 : 0);

  const starSize = cn(
    size === 'sm' && 'h-3.5 w-3.5',
    size === 'md' && 'h-4 w-4',
    size === 'lg' && 'h-5 w-5',
  );

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <div className="flex items-center">
        {/* Full stars */}
        {Array.from({ length: fullStars }).map((_, i) => (
          <Star
            key={`full-${i}`}
            className={cn(starSize, 'fill-amber-400 text-amber-400')}
          />
        ))}
        
        {/* Half star */}
        {hasHalfStar && (
          <div className="relative">
            <Star className={cn(starSize, 'text-earth-200')} />
            <div className="absolute inset-0 overflow-hidden w-1/2">
              <Star className={cn(starSize, 'fill-amber-400 text-amber-400')} />
            </div>
          </div>
        )}
        
        {/* Empty stars */}
        {Array.from({ length: emptyStars }).map((_, i) => (
          <Star
            key={`empty-${i}`}
            className={cn(starSize, 'text-earth-200')}
          />
        ))}
      </div>
      
      {showValue && (
        <span className={cn(
          'font-medium text-earth-700',
          size === 'sm' && 'text-xs',
          size === 'md' && 'text-sm',
          size === 'lg' && 'text-base',
        )}>
          {rating.toFixed(1)}
        </span>
      )}
    </div>
  );
};

export default StarRating;
